import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { LiabilityModel } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models';
import {
  bankingLiabilityAccountTypes,
  heldByTypes,
  liabilityTypes,
  mortgageTypes,
} from 'apps/cmg-clear2-ui/src/app/cmg-1003/models/lookups/assets.lookups';
import { Cmg1003ApplicationInfoQuery, Cmg1003AssetsStore } from 'apps/cmg-clear2-ui/src/app/loan-state';
import { Subscription } from 'rxjs';

import { InputText } from 'primeng/inputtext';
import { CmgFormControlDomDirective } from '../../../directives/cmg-form-control-dom.directive';
import { Select } from 'primeng/select';
import { InputNumber } from 'primeng/inputnumber';
import { CmgPrimeNgInputNumberFocusDirective } from '../../../directives/cmg-input-number-focus.directive';
import { ButtonDirective } from 'primeng/button';
import { QaSlugPipe } from '../../../pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-liability-details',
  templateUrl: './liability-details.component.html',
  styleUrls: ['./liability-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputText,
    CmgFormControlDomDirective,
    Select,
    InputNumber,
    CmgPrimeNgInputNumberFocusDirective,
    ButtonDirective,
    QaSlugPipe,
  ],
})
export class LiabilityDetailsComponent implements OnInit, OnChanges, OnDestroy {
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _assetStore = inject(Cmg1003AssetsStore);
  private readonly _appQuery = inject(Cmg1003ApplicationInfoQuery);

  @Input()
  selectedLiability: LiabilityModel;
  readonly editCancelled = output();

  /** Prefix for QA automation IDs. */
  autoIdPrefix? = 'borrowerLiablility-';

  form: UntypedFormGroup;
  liabilityTypes = liabilityTypes;
  bankingLiabilityAccountTypes = bankingLiabilityAccountTypes;
  mortgageTypes = mortgageTypes;
  heldByTypes = heldByTypes;
  accountType: number;
  liabilityType: number;
  otherExpenseType: number;
  rule1 = false;
  rule2 = false;
  rule3 = false;
  rule4 = false;
  rule5 = false;
  rule6 = false;
  rule7 = false;

  private _subscription = new Subscription();

  ngOnInit(): void {
    this.form = this._fb.group(
      {
        liabilityType: [],
        accountType: [],
        accountNumber: [],
        defaultPaymentLeft: [],
        mortgageType: [],
        helocCreditLimit: [],
        held: [],
        otherDebtDescription: [],
        listedOnCreditReport: [],
        resubordinated: [],
        notCounted: [],
        atrNotes: [],
        otherNotes: [],
        qmatrNotes: [],
      },
      { updateOn: 'change' },
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (propName === 'selectedLiability' && this.form) {
        this.selectedLiability = changes.selectedLiability.currentValue;
        this.form.reset();
        this._initForm();
      }
    }
  }

  ngOnDestroy(): void {
    if (this._subscription && !this._subscription.closed) {
      this._subscription.unsubscribe();
    }
  }

  private _resetRules() {
    this.rule1 = false;
    this.rule2 = false;
    this.rule3 = false;
    this.rule4 = false;
    this.rule5 = false;
    this.rule6 = false;
    this.rule7 = false;
    this.form.controls['defaultPaymentLeft'].enable();
    this.form.controls['notCounted'].enable();
  }

  private _setRules() {
    this._resetRules();
    //Banking and All but Installment, Mortgage, HELOC, Other
    if (this.liabilityType === 1 && this.accountType !== 2 && this.accountType !== 3 && this.accountType !== 4 && this.accountType !== 9) {
      this.rule1 = true;
    }
    if (this.liabilityType === 2 && this.otherExpenseType !== 5) {
      this.rule2 = true;
    }
    if (this.liabilityType === 2 && this.otherExpenseType === 5) {
      this.rule3 = true;
    }

    if (this.liabilityType === 1 && this.accountType === 2) {
      //Banking and Installment
      this.rule4 = true;
      this.form.controls['defaultPaymentLeft'].disable();
    }
    if (this.liabilityType === 1 && this.accountType === 3) {
      //Banking and Mortgage
      this.rule5 = true;
      this.form.controls['notCounted'].disable();
    }
    if (this.liabilityType === 1 && this.accountType === 4) {
      //Banking and HELOC
      this.rule6 = true;
      this.form.controls['notCounted'].disable();
    }

    if (this.liabilityType === 1 && this.accountType === 9) {
      //Banking and Other
      this.rule7 = true;
    }

    const otherDebtControl = this.form.get('otherDebtDescription');
    if (this.rule3 || this.rule7) {
      otherDebtControl.setValidators([Validators.required]);
    } else if (otherDebtControl.hasValidator(Validators.required)) {
      otherDebtControl.removeValidators(Validators.required);
      otherDebtControl.setValue('');
    }
  }

  private _initForm() {
    this.form.patchValue({
      liabilityType: this.selectedLiability.liabilityType,
      helocCreditLimit: this.selectedLiability.helocCreditLimit,
      defaultPaymentLeft: this.selectedLiability.defaultPaymentLeft,
      accountNumber: this.selectedLiability.accountNumber,
      mortgageType: this.selectedLiability.mortgageType,
      held: this.selectedLiability.otherAccountHeldByType,
      otherDebtDescription: this.selectedLiability.otherDescription,
      listedOnCreditReport: this.selectedLiability.listedOnCreditReport ? String(this.selectedLiability.listedOnCreditReport) : 'false',
      resubordinated: this.selectedLiability.resubordinated ? String(this.selectedLiability.resubordinated) : 'false',
      notCounted: this.selectedLiability.notCounted ? String(this.selectedLiability.notCounted) : 'false',
      atrNotes: this.selectedLiability.atrNotes,
      otherNotes: this.selectedLiability.otherNotes,
      qmatrNotes: this.selectedLiability.qmatrNotes,
    });

    //for condition check
    this.accountType = this.selectedLiability.accountType;
    this.liabilityType = this.selectedLiability.liabilityType;
    this.otherExpenseType = this.selectedLiability.otherExpenseType; //liability name
    this._setRules();
    this._getPaymentLeftValue();
  }

  private _getPaymentLeftValue() {
    if (this.liabilityType === 1 && this.accountType === 2 && !!this.selectedLiability.monthlyPayment && !!this.selectedLiability.unpaidBalance) {
      this.form.controls['defaultPaymentLeft'].setValue(0);
      if (this.selectedLiability.monthlyPayment !== 0 && this.selectedLiability.unpaidBalance !== 0) {
        const value = this.selectedLiability.unpaidBalance / this.selectedLiability.monthlyPayment;
        if (value !== Math.round(value)) {
          this.form.controls['defaultPaymentLeft'].setValue(Math.round(value + 0.5));
        } else {
          this.form.controls['defaultPaymentLeft'].setValue(Math.round(value));
        }
      }
    }
  }

  updateInfo() {
    const currentRow: LiabilityModel = {
      ...this.selectedLiability,
      helocCreditLimit: this.form.controls['helocCreditLimit'].value,
      defaultPaymentLeft: this.form.controls['defaultPaymentLeft'].value,
      accountNumber: this.form.controls['accountNumber'].value,
      mortgageType: this.form.controls['mortgageType'].value,
      otherAccountHeldByType: this.form.controls['held'].value,
      otherDescription: this.form.controls['otherDebtDescription'].value,
      listedOnCreditReport: this.form.controls['listedOnCreditReport'].value,
      resubordinated: this.form.controls['resubordinated'].value,
      notCounted: this.form.controls['notCounted'].value,
      atrNotes: this.form.controls['atrNotes'].value,
      otherNotes: this.form.controls['otherNotes'].value,
      qmatrNotes: this.form.controls['qmatrNotes'].value,
      unpaidBalance:
        this.liabilityType === 2
          ? this.form.controls['defaultPaymentLeft'].value * this.selectedLiability.monthlyPayment
          : this.selectedLiability.unpaidBalance,
    };
    this._assetStore.updateLiability(this._appQuery.getActiveId() as number, currentRow);
    this._assetStore.update;
    this.form.reset();
    this.editCancelled.emit();
  }

  onCancelClicked() {
    this.editCancelled.emit();
  }
}
